import { WebDataSteps } from "@lvt/web-data";
import { Modal, Typography } from "@material-ui/core";
import { ArrowDropUp } from "@material-ui/icons";
import { DateTime } from "luxon";
import React, { useContext, useMemo, useState } from "react";

import ImageCarousel from "@/common/components/ArchivePage/ImageCarousel/ImageCarousel";
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    useStyles,
} from "@/common/components/ArchivePage/Preset/PresetStyles";
import PresetThumbnail from "@/common/components/ArchivePage/Preset/PresetThumbnail";
import { DraggableTime } from "@/common/components/Draggable/DraggableTime/DraggableTime";
import { LiveUnitMetadataContext } from "@/common/contexts/LiveUnitMetadataContext";
import { setTimelineStart } from "@/store/archives/slice";
import { RootState, useDispatch, useSelector } from "@/store/index";
import { ArchiveImage, ArchiveImagesNavMeta, PresetImages } from "@/utilities/types/archive";

import { HoverImage, HoverImageProps } from "./HoverImage";


interface PresetProps {
    cameraName: string;
    id: number;
    images: PresetImages;
    isDefault: boolean;
    presetName: string | null;
    startExpanded?: boolean;
}
interface ThumbnailData {
    archiveImage: ArchiveImage;
    index: number;
    maxWidth: number | string;
    offset: number;
}

export default function Preset(props: PresetProps) {
    const {
        cameraName,
        id,
        images,
        isDefault,
        presetName,
        startExpanded = false,
    } = props;

    const unitMetadata = useContext<ArchiveImagesNavMeta>(LiveUnitMetadataContext);
    const timelineWidth: number = useSelector((state: RootState) => state.archives.timelineWidth);
    const timelineStart: DateTime = useSelector((state: RootState) => DateTime.fromISO(state.archives.timelineStart, { zone: unitMetadata.timezone }));
    const timeSpan: number = useSelector((state: RootState) => state.archives.timeSpan);
    const [isExpanded, setIsExpanded] = useState<boolean>(startExpanded);
    const [selectedImageIndex, setSelectedImageIndex] = useState<number>(0);
    const [hoverData, setHoverData] = useState<HoverImageProps | null>(null);
    const [isOpen, setIsOpen] = useState(false);
    const [isDragging, setIsDragging] = useState(false);
    const [thumbnailDownPosition, setThumbnailDownPosition] = useState<null | [number, number]>(null);
    const timeDurationMs: number = timeSpan * 1000;

    const dispatch = useDispatch();
    const classes = useStyles();

    const presetImages: ArchiveImage[] = Object.values(images).reduce(
        (sofar: ArchiveImage[], images): ArchiveImage[] => {
            switch (images.type) {
                case WebDataSteps.Success:
                    const payload = images.payload;

                    return [...sofar, ...payload];
                default:
                    // Only care about the images the users is seeing (successes)
                    return sofar;
            }
        },
        [],
    );

    const handleMouseOver = (e: React.MouseEvent<HTMLElement>, img: ArchiveImage) => {
        const newHoverData = {
            url: img.thumbUrl ?? img.imageUrl,
            datetime: DateTime.fromISO(img.datetimeRecorded, { zone: unitMetadata.timezone }),
            thumbnailBoundingBox: e.currentTarget.getBoundingClientRect(),
        };

        setHoverData(newHoverData);
    };

    const thumbnailData = useMemo(() => {
        const pixelsPerSecond = timelineWidth / timeSpan;

        const data: ThumbnailData[] = presetImages.map((img, i) => {
            const datetimeRecorded = DateTime.fromISO(img.datetimeRecorded);

            const maxWidth = presetImages[i + 1]
                ? (DateTime.fromISO(presetImages[i + 1].datetimeRecorded).toSeconds() - datetimeRecorded.toSeconds()) * pixelsPerSecond - 2
                : "auto";

            const offset = datetimeRecorded.diff(timelineStart, "seconds").seconds * pixelsPerSecond;

            return {
                archiveImage: img,
                index: i,
                maxWidth,
                offset,
            };
        });

        return data;
    }, [images, timelineWidth, timeSpan, timelineStart]);

    const renderThumbnails = thumbnailData.map((data) =>
    (<PresetThumbnail
        key={`${data.archiveImage.imageUrl}_${data.archiveImage.datetimeRecorded}_${data.index}`}
        imageUrl={data.archiveImage.thumbUrl ?? data.archiveImage.imageUrl}
        datetimeRecorded={data.archiveImage.datetimeRecorded}
        offset={data.offset}
        maxWidth={data.maxWidth}
        onMouseEnter={(e) => { handleMouseOver(e, data.archiveImage); }}
        onMouseLeave={() => setHoverData(null)}
        onMouseDown={(e) => setThumbnailDownPosition([e.clientX, e.clientY])}
        onMouseUp={(e) => {
            const delta = 5; // An arbitrary distance
            let isClickNotDrag = true;


            if (thumbnailDownPosition) {
                const [x, y] = thumbnailDownPosition;
                const dist = Math.hypot(x - e.clientX, y - e.clientY);

                if (dist > delta) {
                    isClickNotDrag = false;
                }
            }

            if (isClickNotDrag) {
                setSelectedImageIndex(data.index);
                setIsOpen(true);
            }
        }}
    />),
    );

    return (
        <>
            <Accordion data-testid="accordion" square key={id} expanded={isExpanded}>
                <AccordionSummary expandIcon={<ArrowDropUp />} onClick={() => setIsExpanded((prev) => !prev)}>
                    <Typography className={classes.name}>{presetName + (isDefault ? " (Home)" : "")}</Typography>
                </AccordionSummary>
                <AccordionDetails>
                    <DraggableTime
                        className={classes.timeline}
                        style={{ width: timelineWidth, cursor: "ew-resize" }}
                        onDragStart={() => {
                            setIsDragging(true);
                        }}
                        timeUpdate={(dt: DateTime) => {
                            setHoverData(null);
                            dispatch(setTimelineStart(dt.toISO()));
                        }}
                        onDragEnd={() => {
                            setIsDragging(false);
                        }}
                        time={timelineStart}
                        duration={timeDurationMs}
                    >
                        {renderThumbnails}
                        <div style={{ width: timelineWidth }} className={classes.thumbnailLine} />
                    </DraggableTime>
                </AccordionDetails>
            </Accordion>

            {hoverData && !isDragging && (
                <HoverImage {...hoverData} />
            )}
            <Modal open={isOpen} onClose={() => setIsOpen(false)} className={classes.modal}>
                <ImageCarousel
                    cameraName={cameraName}
                    presetName={presetName ?? ""}
                    presetImages={presetImages}
                    selectedImageIndex={selectedImageIndex}
                    setIsOpen={setIsOpen}
                    imageCarouselOpen={isOpen}
                />
            </Modal>
        </>
    );
}
